import React from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { colors } from "../templates/dark-hero/colors";
import { formatNightDateLabel, formatServersLine } from "./cetTime";
import useCountdown from "./useCountdown";

const STEAM_VISIBILITY_NOTICE =
  "To play, your Steam profile must be public with your CS2 hours visible. We check when you join the match night.";

const cardSx = {
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: `1px solid ${colors.border.default}`,
  borderRadius: "18px",
  p: { xs: "24px 20px", md: "32px 28px" },
  display: "flex",
  flexDirection: "column",
  gap: 1,
  transition: "border-color 0.3s",
  "&:hover": { borderColor: "rgba(232,163,61,0.25)" },
};

const dateLabelSx = {
  fontSize: { xs: "0.85rem", md: "0.95rem" },
  fontWeight: 700,
  letterSpacing: "0.06em",
  color: colors.primary.main,
};

const countdownSx = {
  fontSize: "0.8rem",
  color: colors.text.muted,
  mb: 1,
};

const serversLineSx = {
  fontSize: "0.9rem",
  color: colors.text.secondary,
};

const confirmedLineSx = {
  fontSize: "1.05rem",
  fontWeight: 700,
  color: colors.text.primary,
  mt: 1,
};

const capacityLineSx = {
  fontSize: "0.82rem",
  color: colors.text.secondary,
};

const inReviewLineSx = {
  fontSize: "0.8rem",
  color: colors.text.muted,
};

const ctaSx = {
  mt: 2,
  py: 1.2,
  borderRadius: "999px",
  textTransform: "none",
  fontWeight: 700,
  backgroundColor: colors.primary.main,
  color: "#0B0D0F",
  "&:hover": { backgroundColor: colors.primary.hover },
};

const noticeSx = {
  mt: 1.5,
  fontSize: "0.72rem",
  lineHeight: 1.5,
  color: colors.text.muted,
};

export default function MatchNightCard({ night, onReserve }) {
  const scheduledAt = new Date(night.scheduledAt);
  const countdown = useCountdown(scheduledAt);
  const isOn = night.confirmedCount >= night.minToFire;

  return (
    <Box sx={cardSx}>
      <Typography sx={dateLabelSx}>{formatNightDateLabel(scheduledAt, night.region)}</Typography>
      <Typography sx={countdownSx}>{countdown.label}</Typography>
      <Typography sx={serversLineSx}>{formatServersLine(night.region, night.serverLocations)}</Typography>

      <Typography sx={confirmedLineSx}>
        {isOn
          ? `Match night is ON. ${night.confirmedCount} confirmed.`
          : `${night.confirmedCount} of ${night.minToFire} needed to start`}
      </Typography>
      <Typography sx={capacityLineSx}>{night.maxCapacity} spots available</Typography>
      {night.pendingCount > 0 && (
        <Typography sx={inReviewLineSx}>{night.pendingCount} more in review</Typography>
      )}

      <Button sx={ctaSx} onClick={() => onReserve(night)}>
        Reserve your spot
      </Button>
      <Typography sx={noticeSx}>{STEAM_VISIBILITY_NOTICE}</Typography>
    </Box>
  );
}

MatchNightCard.propTypes = {
  night: PropTypes.shape({
    id: PropTypes.string.isRequired,
    scheduledAt: PropTypes.string.isRequired,
    region: PropTypes.string.isRequired,
    serverLocations: PropTypes.arrayOf(PropTypes.string).isRequired,
    minToFire: PropTypes.number.isRequired,
    maxCapacity: PropTypes.number.isRequired,
    confirmedCount: PropTypes.number.isRequired,
    pendingCount: PropTypes.number.isRequired,
  }).isRequired,
  onReserve: PropTypes.func.isRequired,
};
